import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { defectService } from '../../services';

const DefectMaster = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const response = await defectService.getAll({ search: searchTerm });
      setItems(response.data || []);
    } catch (e) {
      console.error('Error loading defects:', e);
      toast.error('Failed to load defects');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
    if (item) {
      reset({ defect: item.defect, description: item.description, status: item.status });
    } else { reset({ defect: '', description: '', status: 1 }); }
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); reset(); };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingItem) {
        await defectService.update(editingItem.id, data);
        toast.success('Defect updated successfully!');
      } else {
        await defectService.create(data);
        toast.success('Defect created successfully!');
      }
      closeModal();
      load();
    } catch (e) {
      console.error('Error saving defect:', e);
      toast.error('Failed to save defect');
    } finally { setLoading(false); }
  };

  const toggleStatus = async (item) => {
    try {
      setLoading(true);
      await defectService.toggleStatus(item.id);
      toast.success('Defect status updated!');
      load();
    } catch (e) {
      console.error('Error toggling status:', e);
      toast.error('Failed to update status');
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>Defects Master</h1>
          <button onClick={() => openModal()} className="btn btn-primary">
            <Plus size={18} />
            Add Defect
          </button>
        </div>

        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input type="text" placeholder="Search defects..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-box" style={{ paddingLeft: '40px' }} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Defect</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: '500' }}>{item.defect}</td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {item.description || '-'}
                    </div>
                  </td>
                  <td>
                    <button onClick={() => toggleStatus(item)} className={`status-badge ${item.status === 1 ? 'status-active' : 'status-inactive'}`} style={{ cursor: 'pointer', border: 'none' }} disabled={loading}>
                      {item.status === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openModal(item)} className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: '12px' }}>
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading defects...</div>)}
        {!loading && items.length === 0 && (<div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No data found.</div>)}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingItem ? 'Edit Defect' : 'Add New Defect'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Defect</label>
                <input type="text" className="form-input" placeholder="Enter defect label" {...register('defect', { required: 'Defect is required', minLength: { value: 2, message: 'Must be at least 2 characters' } })} />
                {errors.defect && (<div className="error-message">{errors.defect.message}</div>)}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" placeholder="Enter description (optional)" rows={4} {...register('description')} />
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" {...register('status', { required: 'Status is required' })}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                {errors.status && (<div className="error-message">{errors.status.message}</div>)}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editingItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefectMaster;
